// Shared behaviour for <Dialog> and <Sheet> (§11b): native <dialog> + showModal() (background inert),
// focus to the first field or heading, Tab focus trap, Esc to close, focus returned to the trigger.
import { useEffect, useLayoutEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';

export function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true',
  );
}

export function useModalDialog(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useLayoutEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      returnTo.current = (document.activeElement as HTMLElement) ?? null;
      dlg.showModal();
      // First field, else the heading (made focusable), else the dialog itself.
      const field = dlg.querySelector<HTMLElement>('[data-autofocus]') ??
        dlg.querySelector<HTMLElement>('input:not([type=hidden]):not([disabled]),textarea:not([disabled]),select:not([disabled])');
      const heading = dlg.querySelector<HTMLElement>('h1,h2,h3');
      const target = field ?? heading ?? dlg;
      if (target === heading && !heading!.hasAttribute('tabindex')) heading!.setAttribute('tabindex', '-1');
      target.focus();
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  // Return focus to the trigger whenever the dialog closes (any path).
  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    const onNativeClose = () => {
      const el = returnTo.current;
      returnTo.current = null;
      if (el && document.contains(el)) el.focus();
    };
    // Esc fires `cancel`: keep React state the source of truth.
    const onCancel = (e: Event) => {
      e.preventDefault();
      onCloseRef.current();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // jsdom and some browsers don't fire `cancel`; handle Esc directly.
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusableIn(dlg);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !dlg.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !dlg.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    dlg.addEventListener('close', onNativeClose);
    dlg.addEventListener('cancel', onCancel);
    dlg.addEventListener('keydown', onKeyDown);
    return () => {
      dlg.removeEventListener('close', onNativeClose);
      dlg.removeEventListener('cancel', onCancel);
      dlg.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // Unmounting while open still returns focus.
  useEffect(
    () => () => {
      const el = returnTo.current;
      if (el && document.contains(el)) el.focus();
    },
    [],
  );

  return ref;
}
